import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowUpRight, ChevronDown, Satellite, Crosshair, Play, Gauge } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { KineticLine, EASE } from '@/components/Reveal';
import GlobeScene from '@/components/GlobeScene';

export default function Hero() {
  const { t, isRTL } = useLanguage();
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const gx = useTransform(sx, (v) => v * 16);
  const gy = useTransform(sy, (v) => v * 12);

  const onMove = (e) => {
    const r = sectionRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const seeSystem = (e) => {
    const target = t('hero.cta2Link');
    if (!target.startsWith('#')) return;
    e.preventDefault();
    if (window.__lenis) window.__lenis.scrollTo('#dashboard-section', { offset: -80 });
    else document.getElementById('dashboard-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} onMouseMove={onMove} onMouseLeave={onLeave}
      className="relative overflow-hidden pb-14 pt-28 lg:pt-32" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-24 start-[8%] h-96 w-96 rounded-full bg-brand-sky/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 end-[5%] h-80 w-80 rounded-full bg-brand-teal/15 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 start-[45%] h-72 w-72 rounded-full bg-brand-violet/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-60" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:gap-6 lg:px-8">
        <div>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-blue/20 bg-white/80 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-brand-blue glass" data-testid="hero-eyebrow-chip">
              <span className="relative flex h-2 w-2">
                <span className="pulse-ring absolute inline-flex h-full w-full rounded-full bg-brand-blue" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-blue" />
              </span>
              {t('hero.chip')}
            </span>
          </motion.div>

          <h1 className="mt-6 font-display text-5xl font-black leading-none tracking-tight text-brand-ink sm:text-6xl lg:text-7xl" data-testid="hero-title">
            <KineticLine delay={0.1}>Wiyak<span className="text-gradient">GPS</span></KineticLine>
          </h1>
          <h2 className="mt-3 font-display text-2xl font-extrabold sm:text-3xl" data-testid="hero-tagline">
            <KineticLine delay={0.24}><span className="text-gradient">{t('hero.tagline')}</span></KineticLine>
          </h2>

          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
            className="mt-6 max-w-lg text-base leading-relaxed text-brand-steel sm:text-lg" data-testid="hero-sub">
            {t('hero.desc')}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4">
            <Link to={t('hero.cta1Link')} data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover hover:shadow-[0_18px_40px_-14px_rgba(0,82,255,0.7)]">
              {t('hero.cta1')}
              <ArrowUpRight className={`h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 ${isRTL ? 'rotate-[-90deg]' : ''}`} />
            </Link>
            <a href={t('hero.cta2Link')} onClick={seeSystem} data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-7 py-3.5 text-sm font-bold text-brand-ink transition-all duration-300 hover:border-brand-blue hover:text-brand-blue">
              <Play className="h-4 w-4" /> {t('hero.cta2')}
            </a>
          </motion.div>

          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            <li className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim"><Satellite className="h-3.5 w-3.5 text-brand-blue" /> {t('hero.sat')}</li>
            <li className="inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-brand-dim"><Crosshair className="h-3.5 w-3.5 text-brand-teal" /> {t('hero.acc')}</li>
          </motion.ul>
        </div>

        <motion.div className="relative h-[440px] sm:h-[520px] lg:h-[580px]" style={{ x: gx, y: gy }}
          initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.3, ease: EASE }}
          data-testid="hero-globe-wrap">
          <GlobeScene />

          <div className="absolute end-2 top-6 flex items-center gap-2 rounded-2xl bg-white/85 px-4 py-3 shadow-lg glass animate-float-y" data-testid="hero-chip-sat">
            <Satellite className="h-4 w-4 text-brand-blue" />
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-brand-ink">{t('hero.sat')}</p>
          </div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="absolute bottom-2 start-2 w-52 overflow-hidden rounded-2xl border border-brand-line bg-white/90 shadow-2xl glass" data-testid="hero-truck-card">
            <img src={t('hero.truckImg')} alt="Tracked truck" className="h-24 w-full object-cover" loading="eager" />
            <div className="flex items-center justify-between p-3">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest text-brand-steel">{t('hero.truck')}</p>
              <p className="inline-flex items-center gap-1 font-mono text-xs font-bold text-brand-blue" dir="ltr">{t('hero.speed')} <Gauge className="h-3 w-3" /></p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
        className="relative mt-8 flex flex-col items-center gap-1 text-brand-dim">
        <span className="font-mono text-[10px] uppercase tracking-widest">{t('hero.scroll')}</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.div>
    </section>
  );
}

